/**
 * Shared bits for the OP Readiness (Stage 7a) views.
 */
import { Badge } from '@/components/ui/badge';

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

const READINESS_VARIANT: Record<string, string> = {
  READY_TO_AUDIT: 'bg-emerald-100 text-emerald-800',
  READY_WITH_WARNINGS: 'bg-amber-100 text-amber-800',
  READY_TO_WRITE: 'bg-emerald-100 text-emerald-800',
  BLOCKED_MAPPING: 'bg-red-100 text-red-800',
  NOT_FOUND_IN_OP: 'bg-slate-200 text-slate-700',
  AUDIT_ERROR: 'bg-red-200 text-red-900',
};

export function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const cls = READINESS_VARIANT[status] ?? 'bg-slate-100 text-slate-700';
  return <Badge variant="outline" className={`border-transparent font-medium ${cls}`}>{status}</Badge>;
}

const SEVERITY_VARIANT: Record<string, string> = {
  critical: 'bg-red-100 text-red-800',
  warning: 'bg-amber-100 text-amber-800',
  info: 'bg-sky-100 text-sky-800',
};

export function SeverityBadge({ severity }: { severity: string }) {
  const cls = SEVERITY_VARIANT[severity] ?? 'bg-slate-100 text-slate-700';
  return <Badge variant="outline" className={`border-transparent ${cls}`}>{severity}</Badge>;
}

export function ValueCell({ value }: { value: string | null | undefined }) {
  const v = value ?? '';
  return (
    <pre className="max-w-[26rem] overflow-hidden text-ellipsis whitespace-pre-wrap break-words font-mono text-xs text-muted-foreground">
      {v === '' ? '∅ (empty)' : v}
    </pre>
  );
}

export function JsonBlock({ value, label }: { value: unknown; label?: string }) {
  return (
    <div className="min-w-0">
      {label && <div className="mb-1 text-xs font-medium text-muted-foreground">{label}</div>}
      <pre className="max-h-[28rem] overflow-auto rounded-md border bg-muted/40 p-3 font-mono text-xs leading-relaxed">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

export function UnavailableNotice({ reason }: { reason: string }) {
  return (
    <div className="rounded-lg border border-dashed p-8 text-center">
      <p className="text-sm font-medium">Canonical platform unavailable</p>
      <p className="mt-2 text-sm text-muted-foreground">{reason}</p>
    </div>
  );
}
